import React, { memo, useCallback, useMemo } from 'react';
import { TextFieldProps } from '@mui/material';
import {
  MobileDateTimePicker,
  MobileDateTimePickerProps,
} from '@mui/x-date-pickers/MobileDateTimePicker';
import { DateTimeValidationError } from '@mui/x-date-pickers/models';
import { useTranslate } from 'hooks/use-translate';
import { APP_FORMAT_DATE_TIME } from 'configs/const';
import { AppPickerField, asPickerTextFieldSlotProps } from 'components/app-picker-field';
import { getPickerValidationError } from 'components/app-picker-field/validation';
import {
  AppPickerActionBar,
  PICKER_MODAL_ACTIONS,
  PickerModalProvider,
  usePickerModal,
} from 'components/app-picker-modal';
import { convertToDate, DateValue, isDateLike } from 'utils/dates';
import { objectSlotProps } from 'utils/slot-props';

/**
 * App date-time picker on @mui/x-date-pickers (Phase D1; was
 * KeyboardDateTimePicker). Same contract as before for its three call sites:
 * `value` string | Date | null in, the picked Date (or null) out through
 * `onChange`, text-field props at the top level routed to the field slot.
 * 24-hour clock, as the app formats (`APP_FORMAT_DATE_TIME` is HH:mm).
 *
 * Phase D2 (x-date-pickers 9): the field is the app's `<input>`-based
 * `AppPickerField`; input / label / helper-text slot props take MUI 9's shape,
 * `slotProps.textField.slotProps.{input,inputLabel,htmlInput,formHelperText}`.
 *
 * Prompt 2a-fix (D-48, D-49): v3's modal model again -- `MobileDateTimePicker`
 * (date, then hours and minutes, Cancel / OK bar), the selection is a draft
 * until OK (`components/app-picker-modal`); `onAccept` never fires from
 * typing; the field's error follows v3's built-in validation.
 */
type PickerProps = Omit<
  MobileDateTimePickerProps,
  | 'value'
  | 'onChange'
  | 'format'
  | 'label'
  | 'name'
  | 'autoFocus'
  | 'disabled'
  | 'readOnly'
  | 'inputRef'
  | 'slotProps'
> & {
  /** The picker's slot props; `textField` is typed as the TextField the app's field renders. */
  slotProps?: Omit<NonNullable<MobileDateTimePickerProps['slotProps']>, 'textField'> & {
    textField?: TextFieldProps;
  };
};

type FieldProps = Pick<
  TextFieldProps,
  | 'label'
  | 'error'
  | 'helperText'
  | 'placeholder'
  | 'onBlur'
  | 'autoFocus'
  | 'size'
  | 'fullWidth'
  | 'required'
  | 'name'
  | 'inputRef'
  | 'style'
  | 'className'
>;

export interface AppDateTimePickerProps extends PickerProps, FieldProps {
  value?: DateValue;
  onChange: (date: Date | null) => void;
  format?: string;
  disabled?: boolean;
  readOnly?: boolean;
  inputVariant?: TextFieldProps['variant'];
}

export const AppDateTimePicker = memo<AppDateTimePickerProps>(
  ({
    onChange,
    value,
    format = APP_FORMAT_DATE_TIME,
    inputVariant,
    label,
    error,
    helperText,
    placeholder,
    onBlur,
    autoFocus,
    size,
    fullWidth = true,
    required,
    name,
    inputRef,
    style,
    className,
    slots,
    slotProps,
    onAccept,
    onOpen,
    onClose,
    ...rest
  }) => {
    const { t } = useTranslate();
    // An invalid Date passes through as it is: the field publishes one while the
    // user types, and giving it back keeps the typed text in the field.
    const pickerValue = useMemo(
      () => (value instanceof Date ? value : isDateLike(value) ? convertToDate(value) : null),
      [value],
    );
    const textFieldSlot = slotProps?.textField;
    const onChangeWrap = useCallback((date: Date | null) => onChange(date), [onChange]);
    const modal = usePickerModal<DateTimeValidationError>({
      value: pickerValue,
      onChange: onChangeWrap,
      onAccept,
      onOpen,
      onClose,
    });
    const { minDate, maxDate, disablePast, disableFuture } = rest;
    const validationError = getPickerValidationError(pickerValue, {
      minDate,
      maxDate,
      disablePast,
      disableFuture,
    });
    const localeText = useMemo(
      () => ({
        todayButtonLabel: t('today'),
        okButtonLabel: t('ok'),
        cancelButtonLabel: t('cancel'),
      }),
      [t],
    );
    // N2 (T5): the modal's toolbar title in Hebrew below md (app-picker-modal)
    const modalLocaleText = useMemo(
      () => ({ ...localeText, ...modal.localeTextBelowMd }),
      [localeText, modal.localeTextBelowMd],
    );
    return (
      <PickerModalProvider actions={modal.actions}>
        <MobileDateTimePicker
          ampm={false}
          {...rest}
          {...modal.pickerProps}
          format={format}
          label={label}
          name={name}
          autoFocus={autoFocus}
          inputRef={inputRef}
          localeText={modalLocaleText}
          slots={{ ...slots, field: AppPickerField, actionBar: AppPickerActionBar }}
          slotProps={{
            ...slotProps,
            actionBar: { actions: PICKER_MODAL_ACTIONS, ...slotProps?.actionBar },
            dialog: { ...slotProps?.dialog, ...modal.dialogSlotProps },
            // v3's keyboard button had no edge; x-date-pickers' edge="end" pulls the field 12px narrower
            openPickerButton: { edge: false, ...slotProps?.openPickerButton },
            textField: asPickerTextFieldSlotProps({
              variant: inputVariant,
              error: Boolean(error) || Boolean(validationError),
              // v3: the consumer's helper text wins; the built-in string fills an empty one.
              helperText: helperText || validationError || helperText,
              onBlur,
              size,
              fullWidth,
              required,
              style,
              className,
              ...textFieldSlot,
              slotProps: {
                ...textFieldSlot?.slotProps,
                htmlInput: { placeholder, ...objectSlotProps(textFieldSlot?.slotProps?.htmlInput) },
              },
            }),
          }}
        />
      </PickerModalProvider>
    );
  },
);
